/* ==========================================================================
   Vildblad 2.0 – lagerstatus, bevaka-formulär, omdömen och formulär utan sidomladdning.
   Bygger vidare på theme.js (window.Vildblad). Ingen extern kod.
   ========================================================================== */
(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const V = window.Vildblad || {};
  const t = (key, fallback) => (V.strings && V.strings[key]) || fallback;

  /* ---------- Formulär som skickas utan att sidan laddas om ----------
     Gäller omdömen och alla e-postformulär (bevaka, kommer snart, nyhetsbrev).
     Shopify svarar med samma sida där ALLA e-postformulär står i skickat läge, så vi byter
     bara ut det formulär som kunden faktiskt skickade – annars skulle t.ex. nyhetsbrevet i
     sidfoten få bevaka-rutan att säga "Tack!". Vill Shopify visa en robotkontroll skickas
     formuläret på vanligt sätt i stället. */
  const isAjaxForm = (form) => form.hasAttribute('data-ajax-form')
    || (form.id && form.querySelector('input[name="form_type"][value="customer"]'));
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form');
    if (!form || !isAjaxForm(form) || form.dataset.native === 'true') return;
    if (!form.checkValidity()) return; // låt webbläsaren visa felen
    e.preventDefault();
    const btn = $('button[type="submit"]', form);
    const msg = $('[data-form-message]', form);
    btn?.classList.add('is-loading');
    btn?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
      if (/\/challenge/.test(res.url)) { form.dataset.native = 'true'; HTMLFormElement.prototype.submit.call(form); return; }
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = form.id && doc.getElementById(form.id);
      if (!fresh) throw new Error('no-form');
      fresh.setAttribute('data-ajax-form', '');
      form.replaceWith(fresh);
      const restock = fresh.closest('[data-restock]');
      if (restock && fresh.querySelector('.form-status--success')) restock.dataset.posted = restock.dataset.variant || 'yes';
      const status = $('.form-status', fresh);
      if (status) { status.setAttribute('tabindex', '-1'); status.focus({ preventScroll: false }); }
    } catch (err) {
      btn?.classList.remove('is-loading');
      btn?.removeAttribute('aria-busy');
      if (msg) { msg.textContent = t('formError', 'Något gick fel. Försök igen eller mejla oss.'); msg.hidden = false; }
      else { form.dataset.native = 'true'; HTMLFormElement.prototype.submit.call(form); }
    }
  });

  /* ---------- Produktsida: lagerstatus och bevaka slutsåld variant ----------
     Bevaka-formuläret taggar kunden med bevaka-<produkt>-<variant-id>. Har kunden bevakat en
     variant och byter till en annan slutsåld variant visas ett nytt, tomt formulär i stället
     för tacket. */
  const setRestockTags = (restock, variantId) => {
    const tags = $('[data-restock-tags]', restock);
    if (!tags) return;
    const base = tags.dataset.base || '';
    const last = base.split(',').pop().trim();
    tags.value = variantId ? `${base}, ${last}-${variantId}` : base;
  };
  $$('[data-restock]').forEach((restock) => {
    const form = $('form', restock);
    if (form && $('[data-restock-tags]', form)) restock._pristine = form.outerHTML;
    const section = restock.closest('[data-product-section]');
    const id = section && $('form[data-product-form] [name="id"]', section)?.value;
    if (id) { restock.dataset.variant = id; setRestockTags(restock, id); }
  });

  document.addEventListener('variant:change', (e) => {
    const section = e.target.closest('[data-product-section]') || e.target;
    const { variant } = e.detail || {};
    const json = $('[data-product-json]', section);
    const strings = (() => { try { return JSON.parse(json.textContent).strings || {}; } catch (_) { return {}; } })();
    const stock = $('[data-stock]', section);
    if (stock) {
      const state = !variant ? 'out' : (variant.stock || (variant.available ? 'in' : 'out'));
      stock.className = `stock stock--${state}`;
      const text = $('[data-stock-text]', stock);
      if (text && strings.stock) text.textContent = !variant ? strings.stock.unavailable : strings.stock[state];
    }
    const restock = $('[data-restock]', section);
    if (restock) {
      restock.hidden = !variant || variant.available;
      if (variant) {
        const id = String(variant.id);
        if (restock.dataset.posted && restock.dataset.posted !== id && restock._pristine) {
          const current = $('form', restock);
          if (current) current.outerHTML = restock._pristine;
          delete restock.dataset.posted;
        }
        restock.dataset.variant = id;
        setRestockTags(restock, id);
      }
    }
  });

  // Antal: håll värdet inom 1–99 även när man skriver för hand
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const n = Math.min(99, Math.max(1, parseInt(input.value, 10) || 1));
    input.value = n;
  });

  /* ---------- Omdömen ---------- */
  function initReviews(root) {
    const panel = $('[data-review-panel]', root);
    const openers = $$('[data-review-open]', root);
    const setOpen = (open, focus = true) => {
      if (!panel) return;
      panel.hidden = !open;
      openers.forEach((b) => b.setAttribute('aria-expanded', String(open)));
      if (open && focus) {
        const first = $('input:not([type="hidden"]), textarea', panel);
        panel.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        setTimeout(() => first?.focus({ preventScroll: true }), 350);
      }
    };
    openers.forEach((b) => b.addEventListener('click', () => setOpen(panel.hidden)));
    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-review-close]')) { setOpen(false, false); openers[0]?.focus(); }
    });
    // Efter ett vanligt (icke-ajax) inskick: visa tack eller fel direkt
    if (panel && $('[data-review-posted], .form-status--error:not([hidden])', panel)) setOpen(true, false);

    const list = $('[data-review-list]', root);
    if (!list) return;
    const size = parseInt(list.dataset.pageSize, 10) || 5;
    const more = $('[data-review-more]', root);
    let shown = size;
    const paint = () => {
      $$('[data-review]', list).forEach((li, i) => { li.hidden = i >= shown; });
      if (more) more.hidden = shown >= list.children.length;
    };
    const sort = (mode) => {
      const items = $$('[data-review]', list);
      items.sort((a, b) => {
        const da = +a.dataset.date || 0; const db = +b.dataset.date || 0;
        const ra = parseFloat(a.dataset.rating) || 0; const rb = parseFloat(b.dataset.rating) || 0;
        if (mode === 'high') return rb - ra || db - da;
        if (mode === 'low') return ra - rb || db - da;
        return db - da;
      });
      items.forEach((li) => list.appendChild(li));
      paint();
    };
    $('[data-review-sort]', root)?.addEventListener('change', (e) => sort(e.target.value));
    more?.addEventListener('click', () => {
      const firstNew = list.children[shown];
      shown += size;
      paint();
      firstNew?.setAttribute('tabindex', '-1');
      firstNew?.focus();
    });
    sort('new');
  }
  $$('[data-reviews]').forEach(initReviews);
  document.addEventListener('shopify:section:load', (e) => $$('[data-reviews]', e.target).forEach(initReviews));
})();

/* Bildräknare i galleriet på mobil (när produkten har många bilder) */
(() => {
  document.querySelectorAll('[data-gallery-count]').forEach((count) => {
    const section = count.closest('[data-product-section]');
    const list = section && section.querySelector('[data-gallery-list]');
    const current = count.querySelector('[data-gallery-current]');
    if (!list || !current) return;
    let raf = 0;
    list.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const items = list.children;
        const center = list.scrollLeft + list.clientWidth / 2;
        let idx = 0;
        for (let i = 0; i < items.length; i++) { if (items[i].offsetLeft - list.offsetLeft <= center) idx = i; }
        current.textContent = String(idx + 1);
      });
    }, { passive: true });
  });
})();